import Markdown from '@/components/content/markdown/Markdown';
import IncidentActions from '../components/IncidentActions';
import IncidentState from '../components/IncidentState';

import { formatIsoDate } from '@/utils/date';
import { createHelper } from '../../issue/utils/helper';
import { getKeys, getValues, objectHasValues } from '@/utils';

import type { Module } from '../../issue/types/modulesv2';
import type { IncidentPageTableRecord } from '../types';
import Priority from '@/components/content/priority/Priority';
import classes from '../components/IncidentModal.module.css';

const helper = createHelper<IncidentPageTableRecord>();

const InlineField = ({ label, value }: { label: string; value: string | number }) => (
  <p className={classes.detailLine}>
    <span className={classes.summaryLabel}>{label}</span> {value}
  </p>
);

const StackedField = ({ label, value }: { label: string; value: string | number }) => (
  <>
    <p className={classes.summaryFieldTitle}>{label}</p>
    <p className={classes.summaryFieldValue}>{value}</p>
  </>
);

export const incidentModalModules = ({
  hideActions,
}: {
  hideActions?: boolean;
}): Array<Module<IncidentPageTableRecord>> => [
  {
    id: 'overview',
    className: classes.summaryCard,
    direction: 'row',
    wrap: 'wrap',
    gap: 0,
    submodules: [
      helper.accessor((data) => data.created_at, {
        className: classes.summaryTopField,
        element: ({ getValue }) => (
          <InlineField label="Created:" value={formatIsoDate(getValue()) || '-'} />
        ),
        scroll: false,
      }),
      helper.accessor((data) => data.updated_at, {
        className: classes.summaryTopField,
        element: ({ getValue }) => (
          <InlineField label="Last Update:" value={formatIsoDate(getValue()) || '-'} />
        ),
        scroll: false,
      }),
      helper.accessor((data) => data?.client_state?.[0]?.assignee, {
        className: classes.summaryDetailField,
        element: ({ getValue }) => (
          <StackedField label="Assignee:" value={getValues(getValue())[0] || 'Unassigned'} />
        ),
        scroll: false,
      }),
      helper.accessor((data) => data.sources, {
        className: classes.summaryDetailField,
        element: ({ getValue }) => (
          <StackedField label="Source:" value={getValues(getValue())[0] || '-'} />
        ),
        scroll: false,
      }),
      helper.accessor((data) => ({ category: data.category, subcategory: data.subcategory }), {
        className: classes.summaryDetailField,
        element: ({ getValue }) => (
          <>
            <p className={classes.summaryFieldTitle}>Category: {getValue().category || '-'}</p>
            <p className={classes.summaryFieldValue}>{getValue().subcategory || '-'}</p>
          </>
        ),
        scroll: false,
      }),
      helper.accessor((data) => data.priority, {
        className: classes.summaryFooterField,
        element: ({ getValue, id }) => (
          <>
            <p className={classes.summaryFooterLabel}>Priority:</p>
            <Priority priority={getValue()} id={+id} />
          </>
        ),
        scroll: false,
      }),
      helper.accessor(
        (data) => ({
          state: data.client_state?.[0],
          hasTakedownRequests:
            data.rfis?.filter((rfi) => rfi.name.includes('Takedown Request')).length > 0,
        }),
        {
          className: `${classes.summaryFooterField} ${classes.summaryStatusField}`,
          element: ({ getValue }) => (
            <>
              <p className={classes.summaryFooterLabel}>Status:</p>
              <IncidentState {...getValue()} />
            </>
          ),
          scroll: false,
        }
      ),
    ],
  },
  {
    removeBackground: true,
    removePadding: true,
    hide: hideActions,
    noPrint: true,
    submodules: [
      helper.accessor((data) => data, {
        className: classes.actionsSection,
        element: ({ getValue }) => {
          const { id, client_state: clientState, clients, rfis, takedowns } = getValue();

          return (
            <div className={classes.actionsRow}>
              <h2 className={classes.actionsTitle}>Actions:</h2>
              <IncidentActions
                issueId={id}
                clientState={clientState?.[0]}
                hasTakedownRequests={
                  rfis?.filter((rfi) => rfi.name.includes('Takedown Request')).length > 0 ||
                  objectHasValues(takedowns)
                }
                clientIds={getKeys(clients) as Array<string>}
                asButton
                buttonVariant="compactOutline"
              />
            </div>
          );
        },
        scroll: false,
      }),
    ],
  },
  {
    className: classes.descriptionCard,
    submodules: [
      helper.accessor((data) => data.description, {
        element: ({ getValue }) => (
          <div className={classes.descriptionContent}>
            <h2 className={classes.sectionTitle}>Description</h2>
            <div className={classes.descriptionMarkdown}>
              <Markdown text={getValue()} disallowedElements={['img']} />
            </div>
          </div>
        ),
        scroll: false,
      }),
    ],
  },
];
